import type { TFunction } from "i18next";
import { match, P } from "ts-pattern";

import { dayjs, PlaceOfSafetyTypeSchema } from "@thalamos/common";
import {
  AuditTrailEntrySchema,
  IncidentActivityAuditTypes,
  tk,
  type AdminReportingFieldKey,
  type AuditTrailEntry,
  type AuditTrailEntryUnbranded,
  type TranslationKey,
} from "@thalamos/vision-common";

import { POS_TYPE_OPTION_KEYS } from "../../AdminEditReportingFields/helpers.js";

export interface EntryDetailRow {
  label: string;
  value: string;
}

type IncidentActivityEntry = Extract<
  AuditTrailEntry,
  { type: (typeof IncidentActivityAuditTypes)[number] }
>;

function isIncidentActivityEntry(
  entry: AuditTrailEntry,
): entry is IncidentActivityEntry {
  return (IncidentActivityAuditTypes as readonly string[]).includes(entry.type);
}

const REPORTING_FIELD_LABELS = {
  dateTimeOfDetention: tk("incidentSummary.entryDetailFields.detentionTime"),
  arrivalDateTime: tk("incidentSummary.entryDetailFields.arrivalTime"),
  dateTimeLeft: tk("incidentSummary.entryDetailFields.leftTime"),
  leavePos: tk("incidentSummary.entryDetailFields.leftEarlyEndTime"),
} satisfies Record<AdminReportingFieldKey, TranslationKey>;

function formatDateTime(iso: string): string {
  return dayjs.utc(iso).tz("Europe/London").format("DD/MM/YYYY HH:mm");
}

/**
 * The details of an incident activity entry as label/value rows for display.
 *
 * Each entry type chooses the fields it shows, so identifiers, internal bookkeeping and
 * free text in the audit data stay hidden unless a type deliberately shows them; an entry
 * that does not match its schema shows no rows rather than its raw data.
 */
export function getEntryDetailRows(
  entry: AuditTrailEntryUnbranded,
  t: TFunction,
): EntryDetailRow[] {
  const parsed = AuditTrailEntrySchema.safeParse(entry);
  if (!parsed.success || !isIncidentActivityEntry(parsed.data)) {
    return [];
  }

  const row = (
    label: TranslationKey,
    value: string | null | undefined,
  ): EntryDetailRow[] => (value ? [{ label: t(label), value }] : []);

  const yesNo = (value: boolean) =>
    t(value ? tk("common.yes") : tk("common.no"));

  const change = (from: string | null | undefined, to: string) =>
    t(tk("incidentSummary.entryDetailFields.change"), {
      from: from ?? t(tk("adminEditReportingFields.notSet")),
      to,
    });

  const placeOfSafetyTypeLabel = (value: string | null | undefined) => {
    if (value === null || value === undefined) {
      return undefined;
    }
    const type = PlaceOfSafetyTypeSchema.safeParse(value);
    // The audit holds whatever type the form had, which need not be in the curated list;
    // show an unlisted one as stored.
    return type.success ? t(POS_TYPE_OPTION_KEYS[type.data]) : value;
  };

  const nhsRows = (data: {
    userName: string;
    userEmail: string;
    teamName?: string;
    teamEmail?: string;
    organisationName?: string;
  }) => [
    ...row(tk("incidentSummary.entryDetailFields.nhsUser"), data.userName),
    ...row(
      tk("incidentSummary.entryDetailFields.nhsUserEmail"),
      data.userEmail,
    ),
    ...row(tk("incidentSummary.entryDetailFields.nhsTeam"), data.teamName),
    ...row(
      tk("incidentSummary.entryDetailFields.nhsTeamEmail"),
      data.teamEmail,
    ),
    ...row(
      tk("incidentSummary.entryDetailFields.nhsOrganisation"),
      data.organisationName,
    ),
  ];

  return match(parsed.data)
    .with({ type: "createIncident" }, ({ data }) =>
      row(
        tk("incidentSummary.entryDetailFields.createdOnBehalf"),
        yesNo(data.isOnBehalfOfSomeoneElse),
      ),
    )
    .with({ type: "formShared" }, ({ data }) => [
      ...row(
        tk("incidentSummary.entryDetailFields.placeOfSafety"),
        data.posName,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.shareMethod"),
        t(
          match(data.shareType)
            .with("email", () =>
              tk("incidentSummary.entryDetailFields.shareMethodEmail"),
            )
            .with("shareCode", () =>
              tk("incidentSummary.entryDetailFields.shareMethodShareCode"),
            )
            .exhaustive(),
        ),
      ),
    ])
    .with(
      {
        type: P.union(
          "createPlaceOfSafety",
          "preTriageEndIncident",
          "preEndIncident",
          "finishForm",
          "endIncident",
          "transferIncident",
          "resolveTransfer",
          "revokeTransfer",
        ),
      },
      () => [],
    )
    .with({ type: "officerRecordRequestPolicePresence" }, ({ data }) => [
      ...row(
        tk("incidentSummary.entryDetailFields.policePresenceRequested"),
        data.hasPolicePresenceRequested?.valueLabel,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.policePresenceRequestedReason"),
        data.policePresenceRequestedReason?.valueLabel,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.remainedAtPlaceOfSafety"),
        data.hasRemainedAtPlaceOfSafety?.valueLabel,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.officerInvolved"),
        data.officerName?.value,
      ),
    ])
    .with({ type: "aspireReviewIncident" }, ({ data }) => nhsRows(data))
    .with({ type: "aspireAcceptIncident" }, ({ data }) => [
      ...nhsRows(data),
      ...row(
        tk("incidentSummary.entryDetailFields.placeOfSafety"),
        data.placeOfSafety?.name,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.placeOfSafetyAddress"),
        data.placeOfSafety &&
          [data.placeOfSafety.address, data.placeOfSafety.postalCode]
            .filter(Boolean)
            .join(", "),
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.isCurrentPlaceOfSafety"),
        data.isCurrentPlaceOfSafety === undefined
          ? undefined
          : yesNo(data.isCurrentPlaceOfSafety),
      ),
    ])
    .with({ type: "aspireRejectIncident" }, ({ data }) => [
      ...nhsRows(data),
      ...row(
        tk("incidentSummary.entryDetailFields.rejectionReason"),
        data.reason,
      ),
    ])
    .with({ type: "aspireRequestPolicePresence" }, ({ data }) => [
      ...nhsRows(data),
      ...row(
        tk("incidentSummary.entryDetailFields.policePresenceRequested"),
        data.policePresenceRequested.hasPolicePresenceRequested.valueLabel,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.policePresenceRequestedReason"),
        data.policePresenceRequested.policePresenceRequestedReason.valueLabel,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.remainedAtPlaceOfSafety"),
        data.recordPolicePresenceData?.hasRemainedAtPlaceOfSafety.valueLabel,
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.officerInvolved"),
        data.recordPolicePresenceData?.officerName?.value,
      ),
    ])
    .with({ type: "aspireNotRequestPolicePresence" }, ({ data }) => [
      ...nhsRows(data),
      ...row(
        tk("incidentSummary.entryDetailFields.policePresenceRequested"),
        t(
          match(data.policePresenceRequested.value)
            .with("no", () => tk("common.no"))
            .with("already-left", () => tk("requestPolicePresence.alreadyLeft"))
            .exhaustive(),
        ),
      ),
      ...row(
        tk("incidentSummary.entryDetailFields.remainedAtPlaceOfSafety"),
        data.recordPolicePresenceData?.hasRemainedAtPlaceOfSafety.valueLabel,
      ),
    ])
    .with({ type: "adminEditReportingFields" }, ({ data }) => [
      ...row(tk("incidentSummary.entryDetailFields.ccmNumber"), data.ccmNumber),
      ...data.edits.flatMap((edit) =>
        row(
          REPORTING_FIELD_LABELS[edit.fieldKey],
          change(
            edit.previousValue === null
              ? null
              : formatDateTime(edit.previousValue),
            formatDateTime(edit.newValue),
          ),
        ),
      ),
    ])
    .with({ type: "adminRelinkPlaceOfSafety" }, ({ data }) => [
      ...row(tk("incidentSummary.entryDetailFields.ccmNumber"), data.ccmNumber),
      ...data.relinks.flatMap((relink) => {
        const newType = placeOfSafetyTypeLabel(relink.newPlaceOfSafetyType);
        return [
          ...row(
            tk("incidentSummary.entryDetailFields.placeOfSafety"),
            relink.newPlaceOfSafety &&
              change(
                relink.previousPlaceOfSafety?.name,
                relink.newPlaceOfSafety.name,
              ),
          ),
          ...row(
            tk("incidentSummary.entryDetailFields.placeOfSafetyType"),
            newType &&
              change(
                placeOfSafetyTypeLabel(relink.previousPlaceOfSafetyType),
                newType,
              ),
          ),
        ];
      }),
    ])
    .exhaustive();
}
